import { useRef, useState } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Upload, Trash2, Loader2 } from "lucide-react";

/**
 * Generic image-upload card.
 *  - postPath: backend POST endpoint (e.g. "/band/logo" or "/players/{id}/photo")
 *  - deletePath: backend DELETE endpoint (same shape)
 *  - currentImage: rendered preview (any React node) — usually <BandLogo/> or <PlayerPhoto/>
 *  - onChange: called after successful upload/delete with a fresh "bust" key
 *  - testIdPrefix: data-testid prefix
 */
export const ImageUploadCard = ({
  postPath,
  deletePath,
  currentImage,
  hasImage,
  onChange,
  testIdPrefix = "image-upload",
  helpText = "PNG, JPEG or WebP, max 5MB",
}) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File exceeds 5MB limit");
      return;
    }
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Only PNG, JPEG, WebP or GIF images are allowed");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await apiClient.post(postPath, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded");
      onChange?.(Date.now());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!deletePath) return;
    if (!window.confirm("Remove this image?")) return;
    setBusy(true);
    try {
      await apiClient.delete(deletePath);
      toast.success("Image removed");
      onChange?.(Date.now());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove image");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="flex-shrink-0">{currentImage}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-2">{helpText}</p>
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePick}
            disabled={busy}
            data-testid={`${testIdPrefix}-upload-btn`}
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {hasImage ? "Replace" : "Upload"}
          </Button>
          {hasImage && deletePath && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              disabled={busy}
              className="text-destructive hover:text-destructive"
              data-testid={`${testIdPrefix}-remove-btn`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove
            </Button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={handleFile}
        data-testid={`${testIdPrefix}-input`}
      />
    </div>
  );
};
